import React from 'react';
import { View, ScrollView, Dimensions } from 'react-native';
import {
  heightPercentageToDP as hp,
  widthPercentageToDP as wp,
} from 'react-native-responsive-screen';
import { KeyboardAccessoryNavigation } from 'react-native-keyboard-accessory';

import Header from '../Header';
import Footer from '../Footer';

const screenHeight = Dimensions.get('window').height;

/**
 * ReportLayout - Unified layout component for report detail screens
 *
 * Handles the differences between web and mobile layouts:
 * - Web: ScrollView wraps the entire content area
 * - Mobile: ScrollView is inside the content area, with KeyboardAccessoryNavigation
 *
 * @param {Object} props
 * @param {boolean} props.isWeb - Whether running on web platform
 * @param {Object} props.containerStyle - Container style from useResponsiveStyles
 * @param {Object} props.headerProps - Props to pass to Header component
 * @param {Object} props.footerProps - Props to pass to Footer component
 * @param {React.ReactNode} props.children - Content to render
 * @param {boolean} [props.showKeyboardAccessory=true] - Whether to show keyboard nav on mobile
 */
const ReportLayout = ({
  isWeb,
  containerStyle,
  headerProps,
  footerProps,
  children,
  showKeyboardAccessory = true,
}) => {
  const contentPadding = {
    paddingHorizontal: wp(6),
    paddingTop: hp(1.7),
  };

  const webHeight = (screenHeight * 0.8) / 1;
  const mobileHeight = screenHeight / 1;

  if (isWeb) {
    return (
      <View style={[containerStyle, { height: webHeight }]}>
        <Header {...headerProps} />

        <ScrollView style={{ height: webHeight }}>
          <View
            style={{
              flex: 1,
              ...contentPadding,
              height: screenHeight / 1,
            }}
          >
            {children}
          </View>
        </ScrollView>

        <Footer {...footerProps} />
      </View>
    );
  }

  // Mobile layout
  return (
    <View style={[containerStyle, { height: mobileHeight }]}>
      <Header {...headerProps} />

      <View
        style={{
          flex: 1,
          ...contentPadding,
          height: mobileHeight,
        }}
      >
        <ScrollView contentContainerStyle={{}}>
          {children}
        </ScrollView>
      </View>

      <Footer {...footerProps} />
      {showKeyboardAccessory && (
        <KeyboardAccessoryNavigation androidAdjustResize avoidKeyboard />
      )}
    </View>
  );
};

export default ReportLayout;
